import { Activity, ArrowUpRight, CheckCircle2, ShieldCheck, Zap } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";

const FEATURES = [
  { icon: Activity, title: "Always informed", copy: "Webapp availability, response times and incidents at a glance." },
  { icon: ShieldCheck, title: "Built for clarity", copy: "SSL, DNS and domain expiry insights in one calm workspace." },
  { icon: Zap, title: "Know sooner", copy: "Timely alerts when something needs your attention." },
];

export default function AuthShell({ title, subtitle, children, footer }) {
  const reducedMotion = useReducedMotion();
  return (
    <div className="min-h-screen grid bg-[#F7F9FC] lg:grid-cols-[1.03fr_1fr]">
      <section className="relative hidden min-h-screen overflow-hidden border-r border-[#e7ecf3] bg-[#edf3fa] px-10 py-10 text-[#243247] lg:flex lg:flex-col lg:justify-between xl:px-16 xl:py-14">
        <div aria-hidden="true" className="pointer-events-none absolute -left-32 top-40 h-96 w-96 rounded-full bg-[#d5e3f5]/70 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -right-36 -top-24 h-[32rem] w-[32rem] rounded-full bg-white/80 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <img src="/monitorflux-mf-original.png" alt="MonitorFlux logo" className="h-12 w-16 shrink-0 object-contain" />
          <span className="font-heading text-xl font-semibold tracking-tight">MonitorFlux</span>
        </div>
        <div className="relative max-w-xl py-14">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#d5e2f2] bg-white/65 px-3 py-1.5 text-xs font-medium text-[#536d9f]">
            <CheckCircle2 size={13} /> Confidence in every check
          </span>
          <h2 className="mt-7 font-heading text-4xl font-semibold leading-[1.16] tracking-tight text-[#243247] xl:text-5xl">
            Peace of mind for every webapp you manage.
          </h2>
          <p className="mt-5 max-w-md text-base leading-7 text-[#66788f]">
            A quieter way to stay on top of uptime, performance and the details that matter.
          </p>
          <div className="mt-9 grid gap-3">
            {FEATURES.map(({ icon: Icon, title: featureTitle, copy }) => (
              <div key={featureTitle} className="flex gap-4 rounded-2xl border border-white/80 bg-white/65 p-4 shadow-[0_6px_24px_rgba(37,59,91,0.035)] backdrop-blur-sm">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#e9f0f9] text-[#536d9f]">
                  <Icon size={19} strokeWidth={1.8} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#2c3d55]">{featureTitle}</p>
                  <p className="mt-1 text-xs leading-5 text-[#718198]">{copy}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <p className="relative text-xs text-[#7a899b]">
          Product By <a href="https://www.omaxtech.com" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#536d9f] hover:underline">Omaxtech Technologies <ArrowUpRight size={12} className="inline" /></a>
        </p>
      </section>

      <section className="flex min-h-screen items-center justify-center px-5 py-12 sm:px-10 lg:px-14">
        <motion.div
          initial={reducedMotion ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reducedMotion ? 0 : 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-[440px]"
        >
          <div className="mb-9 flex items-center gap-3 lg:hidden">
            <img src="/monitorflux-mf-original.png" alt="MonitorFlux logo" className="h-10 w-14 shrink-0 object-contain" />
            <span className="font-heading text-xl font-semibold tracking-tight text-[#243247]">MonitorFlux</span>
          </div>
          <div className="rounded-[24px] border border-[#e7ecf3] bg-white px-6 py-9 shadow-[0_18px_60px_rgba(35,55,85,0.055)] sm:px-10 sm:py-11">
            <div className="mb-2 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#eff4fb] text-[#536d9f]"><ShieldCheck size={22} strokeWidth={1.8}/></div>
            <h1 className="mt-5 font-heading text-3xl font-semibold tracking-tight text-[#243247]">{title}</h1>
            <p className="mt-2 text-sm leading-6 text-[#6b7c91]">{subtitle}</p>
            <div className="mt-8">{children}</div>
            {footer && <div className="mt-7 text-center text-sm text-[#738298]">{footer}</div>}
          </div>
          <p className="mt-7 text-center text-xs text-[#8a98aa]">
            Product By <a href="https://www.omaxtech.com" target="_blank" rel="noopener noreferrer" className="font-medium text-[#536d9f] hover:underline" data-testid="omaxtech-link-auth">Omaxtech Technologies</a>
          </p>
        </motion.div>
      </section>
    </div>
  );
}
